const BASE = import.meta.env.VITE_API_URL ?? "http://localhost:6050";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}

export async function apiFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

export async function apiFetchBlob(path: string, token: string, init?: RequestInit): Promise<Blob> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
  }
  return res.blob();
}

export async function apiUploadFileProxy(token: string, file: File): Promise<{ ok: boolean; fileId: string }> {
  const params = new URLSearchParams({
    name: file.name,
    mime: file.type || "application/octet-stream",
  });

  const res = await fetch(`${BASE}/files/upload-proxy?${params.toString()}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
  }

  return body as { ok: boolean; fileId: string };
}

// ── Typed helpers ────────────────────────────────────────────────────────────

export interface ClipboardItem {
  id: string;
  content: string;
  contentType: string;
  createdAt: number;
  expiresAt: number;
}

export interface SyncItem {
  id: string;
  kind: string;
  key: string;
  value: string;
  updatedAt: number;
}

export interface SyncWriteItem {
  key: string;
  value: string;
  updatedAt?: number;
}

export interface FileRef {
  id: string;
  name: string;
  mime: string;
  size: number;
  surface: string;
  createdAt: number;
  expiresAt: number | null;
}

export interface AuthClaims {
  sub: string;
  role?: "admin" | "user";
  plan?: string;
  features: string[];
  iat: number;
  exp: number;
}

export interface UserProfile {
  id: string;
  username: string | null;
  name: string | null;
  email: string | null;
  role: "admin" | "user";
}

export interface FileInvite {
  id: string;
  status: "pending" | "accepted" | "declined" | "revoked";
  createdAt: number;
  respondedAt: number | null;
  sender?: {
    id: string;
    username: string;
    name: string | null;
    email: string | null;
  };
  recipient?: {
    id: string;
    username: string;
    name: string | null;
    email: string | null;
  };
  file: {
    id: string;
    name: string;
    mime: string;
    size: number;
    surface?: string;
  } | null;
}

export interface RegistryPlugin {
  id: string;
  name: string;
  version: string;
  description: string;
  gitUrl: string;
  authorId: string;
  verified: boolean;
  featured: boolean;
  downloadCount: number;
  publishedAt: number;
  zipUrl: string | null;
  iconUrl: string | null;
  category: string;
  tags: string;
  engine: string;
  latestManifest: string | null;
}

export interface RegistryCategory {
  category: string;
  count: number;
}

export interface PublishPayload {
  name: string;
  version: string;
  description?: string;
  gitUrl: string;
  zipUrl?: string;
  iconUrl?: string;
  category?: string;
  tags?: string;
  engine?: string;
  latestManifest?: string;
}

export interface MqItem {
  id: string;
  action: string;
  params: string;
  target: string;
  priority: number;
  status: "new" | "working" | "failed" | "completed" | "cancelled";
  tryCount: number;
  key: string | null;
  error: string | null;
  result: string | null;
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
}

export interface MqCreatePayload {
  action: string;
  params?: unknown;
  target?: string;
  priority?: number;
  key?: string;
}

export interface ServerWorkspace {
  id: string;
  userId: string;
  name: string;
  icon: string | null;
  updatedAt: number;
  deletedAt: number | null;
}

export interface ServerWorkspaceConfig {
  id: string;
  userId: string;
  workspaceId: string;
  key: string;
  value: string;
  updatedAt: number;
  deletedAt: number | null;
}

export interface WorkspaceWrite {
  id: string;
  name: string;
  icon?: string | null;
  updatedAt?: number;
  deletedAt?: number | null;
}

export interface WorkspaceConfigWrite {
  workspaceId: string;
  key: string;
  value: string;
  updatedAt?: number;
  deletedAt?: number | null;
}

export interface UserVar {
  id: string;
  userId: string;
  key: string;
  value: string; // JSON-encoded
  updatedAt: number;
}

export interface ScheduledTask {
  id: string;
  name: string;
  intervalSec: number | null;
  nextRunAt: number;
  lastRunAt: number | null;
  action: string;
  params: string;
  target: string;
  priority: number;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface ScheduledTaskPayload {
  name: string;
  action: string;
  params?: unknown;
  target?: string;
  priority?: number;
  intervalSec?: number | null;
  nextRunAt?: number;
  enabled?: boolean;
}

export interface WebhookDef {
  id: string;
  name: string;
  secret: string | null;
  action: string;
  params: string;
  target: string;
  priority: number;
  enabled: boolean;
  triggerCount: number;
  lastTriggeredAt: number | null;
  createdAt: number;
}

export interface AuditEntry {
  id: string;
  userId: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  meta: string;
  ip: string | null;
  createdAt: number;
}

export interface AdminUser {
  id: string;
  email: string | null;
  role: "admin" | "user";
  isAdmin: boolean;
  keyIssuedAt: number | null;
  lastSeenAt: number | null;
  createdAt: number;
}

export const api = {
  auth: {
    validate: (token: string) =>
      fetch(`${BASE}/auth/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
        signal: AbortSignal.timeout(8000),
      }).then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
        return body as { valid: boolean; claims: AuthClaims };
      }),
    apiToken: (token: string, ttlSec = 3600) =>
      apiFetch<{ ok: boolean; token: string; tokenType: string; expiresAt: number }>("/auth/api-token", token, {
        method: "POST",
        body: JSON.stringify({ ttlSec }),
      }),
    me: (token: string) => apiFetch<{ user: UserProfile }>("/auth/me", token),
    saveProfile: (token: string, profile: Partial<Pick<UserProfile, "username" | "name" | "email">>) =>
      apiFetch<{ ok: boolean; user: UserProfile }>("/auth/profile", token, {
        method: "POST",
        body: JSON.stringify(profile),
      }),
  },
  clipboard: {
    list: (token: string) => apiFetch<{ items: ClipboardItem[] }>("/clipboard", token),
    push: (token: string, content: string, contentType = "text") =>
      apiFetch<{ id: string }>("/clipboard", token, {
        method: "POST",
        body: JSON.stringify({ content, contentType }),
      }),
    delete: (token: string, id: string) =>
      apiFetch<{ ok: boolean; id: string }>(`/clipboard/${id}`, token, {
        method: "DELETE",
      }),
    clear: (token: string) =>
      apiFetch<{ ok: boolean; cleared: number }>("/clipboard", token, {
        method: "DELETE",
      }),
  },
  sync: {
    scripts: (token: string) => apiFetch<{ items: SyncItem[] }>("/sync/scripts", token),
    settings: (token: string) => apiFetch<{ items: SyncItem[] }>("/sync/settings", token),
    saveSettings: (token: string, items: SyncWriteItem[]) =>
      apiFetch<{ ok: boolean; synced: number }>("/sync/settings", token, {
        method: "POST",
        body: JSON.stringify({ items }),
      }),
  },
  files: {
    list: (token: string) => apiFetch<{ files: FileRef[] }>("/files", token),
    uploadUrl: (token: string, name: string, mime: string, size: number) =>
      apiFetch<{ uploadUrl: string; fileId: string }>("/files/upload-url", token, {
        method: "POST",
        body: JSON.stringify({ name, mime, size }),
      }),
    shareUrl: (token: string, id: string) =>
      apiFetch<{ url: string; expiresAt: number }>(`/files/${id}/share`, token),
    invite: (token: string, id: string, recipientUsername: string) =>
      apiFetch<{ ok: boolean; inviteId: string }>(`/files/${id}/invite`, token, {
        method: "POST",
        body: JSON.stringify({ recipientUsername }),
      }),
    incomingInvites: (token: string) => apiFetch<{ invites: FileInvite[] }>("/files/invites/incoming", token),
    outgoingInvites: (token: string) => apiFetch<{ invites: FileInvite[] }>("/files/invites/outgoing", token),
    acceptInvite: (token: string, inviteId: string) =>
      apiFetch<{
        ok: boolean;
        status: "accepted";
        file: { id: string; name: string; mime: string; size: number };
        download: { url: string; expiresAt: number };
        contact: {
          sender: { username: string; name: string | null; email: string | null };
          recipient: { username: string; name: string | null; email: string | null };
        };
      }>(`/files/invites/${inviteId}/accept`, token, { method: "POST" }),
    declineInvite: (token: string, inviteId: string) =>
      apiFetch<{ ok: boolean; status: "declined" }>(`/files/invites/${inviteId}/decline`, token, {
        method: "POST",
      }),
    revokeInvite: (token: string, inviteId: string) =>
      apiFetch<{ ok: boolean; status: "revoked" }>(`/files/invites/${inviteId}/revoke`, token, {
        method: "POST",
      }),
    delete: (token: string, id: string) =>
      apiFetch<{ ok: boolean }>(`/files/${id}`, token, { method: "DELETE" }),
  },
  push: {
    subscribe: (token: string, subscription: PushSubscriptionJSON) =>
      apiFetch<{ ok: boolean }>("/push/subscribe", token, {
        method: "POST",
        body: JSON.stringify({ subscription }),
      }),
  },
  downloads: {
    bundle: (token: string) => apiFetchBlob("/downloads/bundle", token),
  },
  registry: {
    list: (
      token: string,
      params?: { q?: string; category?: string; verified?: boolean; featured?: boolean; engine?: string; limit?: number }
    ) => {
      const qs = new URLSearchParams();
      if (params?.q) qs.set("q", params.q);
      if (params?.category) qs.set("category", params.category);
      if (params?.verified) qs.set("verified", "true");
      if (params?.featured) qs.set("featured", "true");
      if (params?.engine) qs.set("engine", params.engine);
      if (params?.limit) qs.set("limit", String(params.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return apiFetch<{ plugins: RegistryPlugin[] }>(`/registry/plugins${suffix}`, token);
    },
    categories: (token: string) =>
      apiFetch<{ categories: RegistryCategory[] }>("/registry/categories", token),
    featured: (token: string) =>
      apiFetch<{ plugins: RegistryPlugin[] }>("/registry/featured", token),
    get: (token: string, name: string) =>
      apiFetch<{ plugin: RegistryPlugin }>(`/registry/plugins/${encodeURIComponent(name)}`, token),
    /** Returns the download URL (after redirect, browser handles the zip). */
    downloadUrl: (name: string) =>
      `${BASE}/registry/plugins/${encodeURIComponent(name)}/download`,
    publish: (token: string, payload: PublishPayload) =>
      apiFetch<{ ok: boolean; name: string }>("/registry/plugins", token, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    remove: (token: string, name: string) =>
      apiFetch<{ ok: boolean }>(`/registry/plugins/${encodeURIComponent(name)}`, token, {
        method: "DELETE",
      }),
    verify: (token: string, name: string, verified = true) =>
      apiFetch<{ ok: boolean; name: string; verified: boolean }>(
        `/registry/plugins/${encodeURIComponent(name)}/verify`,
        token,
        { method: "POST", body: JSON.stringify({ verified }) }
      ),
    feature: (token: string, name: string, featured = true) =>
      apiFetch<{ ok: boolean; name: string; featured: boolean }>(
        `/registry/plugins/${encodeURIComponent(name)}/feature`,
        token,
        { method: "POST", body: JSON.stringify({ featured }) }
      ),
  },
  mq: {
    list: (token: string, params?: { status?: string; target?: string; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set("status", params.status);
      if (params?.target) qs.set("target", params.target);
      if (params?.limit) qs.set("limit", String(params.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return apiFetch<{ items: MqItem[] }>(`/mq/items${suffix}`, token);
    },
    create: (token: string, payload: MqCreatePayload) =>
      apiFetch<{ item: MqItem; created: boolean }>("/mq/items", token, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    claim: (token: string, id: string) =>
      apiFetch<{ item: MqItem }>(`/mq/items/${id}/claim`, token, { method: "POST" }),
    complete: (token: string, id: string, result?: unknown) =>
      apiFetch<{ ok: boolean }>(`/mq/items/${id}/complete`, token, {
        method: "POST",
        body: JSON.stringify({ result }),
      }),
    fail: (token: string, id: string, error: string) =>
      apiFetch<{ ok: boolean }>(`/mq/items/${id}/fail`, token, {
        method: "POST",
        body: JSON.stringify({ error }),
      }),
    cancel: (token: string, id: string) =>
      apiFetch<{ ok: boolean }>(`/mq/items/${id}/cancel`, token, { method: "POST" }),
    retry: (token: string, id: string) =>
      apiFetch<{ ok: boolean }>(`/mq/items/${id}/retry`, token, { method: "POST" }),
    delete: (token: string, id: string) =>
      apiFetch<{ ok: boolean }>(`/mq/items/${id}`, token, { method: "DELETE" }),
  },
  workspaces: {
    list: (token: string) =>
      apiFetch<{ workspaces: ServerWorkspace[] }>("/workspaces", token),
    save: (token: string, workspaces: WorkspaceWrite[]) =>
      apiFetch<{ ok: boolean; synced: number }>("/workspaces", token, {
        method: "POST",
        body: JSON.stringify({ workspaces }),
      }),
    listConfigs: (token: string) =>
      apiFetch<{ configs: ServerWorkspaceConfig[] }>("/workspaces/configs", token),
    saveConfigs: (token: string, configs: WorkspaceConfigWrite[]) =>
      apiFetch<{ ok: boolean; synced: number }>("/workspaces/configs", token, {
        method: "POST",
        body: JSON.stringify({ configs }),
      }),
  },
  vars: {
    list: (token: string) => apiFetch<{ vars: UserVar[] }>("/vars", token),
    set: (token: string, key: string, value: unknown) =>
      apiFetch<{ ok: boolean; var: UserVar }>("/vars", token, {
        method: "POST",
        body: JSON.stringify({ key, value }),
      }),
    delete: (token: string, key: string) =>
      apiFetch<{ ok: boolean }>(`/vars/${encodeURIComponent(key)}`, token, { method: "DELETE" }),
  },
  scheduler: {
    list: (token: string) => apiFetch<{ tasks: ScheduledTask[] }>("/scheduler/tasks", token),
    create: (token: string, payload: ScheduledTaskPayload) =>
      apiFetch<{ task: ScheduledTask }>("/scheduler/tasks", token, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    update: (token: string, id: string, payload: Partial<ScheduledTaskPayload>) =>
      apiFetch<{ ok: boolean }>(`/scheduler/tasks/${id}`, token, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    toggle: (token: string, id: string, enabled: boolean) =>
      apiFetch<{ ok: boolean }>(`/scheduler/tasks/${id}/toggle`, token, {
        method: "POST",
        body: JSON.stringify({ enabled }),
      }),
    delete: (token: string, id: string) =>
      apiFetch<{ ok: boolean }>(`/scheduler/tasks/${id}`, token, { method: "DELETE" }),
  },
  webhooks: {
    list: (token: string) => apiFetch<{ webhooks: WebhookDef[] }>("/webhooks", token),
    create: (
      token: string,
      payload: { name: string; action: string; params?: unknown; target?: string; priority?: number; secret?: string }
    ) =>
      apiFetch<{ webhook: WebhookDef }>("/webhooks", token, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    toggle: (token: string, id: string, enabled: boolean) =>
      apiFetch<{ ok: boolean }>(`/webhooks/${id}/toggle`, token, {
        method: "POST",
        body: JSON.stringify({ enabled }),
      }),
    delete: (token: string, id: string) =>
      apiFetch<{ ok: boolean }>(`/webhooks/${id}`, token, { method: "DELETE" }),
  },
  audit: {
    list: (
      token: string,
      params?: { action?: string; resourceType?: string; since?: number; limit?: number; offset?: number }
    ) => {
      const qs = new URLSearchParams();
      if (params?.action) qs.set("action", params.action);
      if (params?.resourceType) qs.set("resourceType", params.resourceType);
      if (params?.since) qs.set("since", String(params.since));
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.offset) qs.set("offset", String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return apiFetch<{ entries: AuditEntry[]; offset: number; limit: number }>(`/audit${suffix}`, token);
    },
  },
  admin: {
    users: (token: string) => apiFetch<{ users: AdminUser[] }>("/admin/users", token),
    upsertUser: (token: string, payload: { userId: string; email?: string | null; isAdmin: boolean }) =>
      apiFetch<{ ok: boolean }>("/admin/users/upsert", token, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    issueKey: (
      token: string,
      payload: { userId?: string; email?: string | null; isAdmin?: boolean; days?: number; features?: string[] }
    ) =>
      apiFetch<{
        ok: boolean;
        token: string;
        claims: AuthClaims;
        user: { id: string; email: string | null; role: "admin" | "user"; isAdmin: boolean; keyIssuedAt: number };
      }>("/admin/keys/issue", token, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    setAdmin: (token: string, userId: string, isAdmin: boolean) =>
      apiFetch<{ ok: boolean }>(`/admin/users/${userId}/admin`, token, {
        method: "POST",
        body: JSON.stringify({ isAdmin }),
      }),
  },
};
